import * as React from 'react';
import type { GetStaticProps } from 'next';
import { queryPageBySlug, queryFooter, queryNavbar, querySectionsBySystemIds } from '@/clients/queries';
import { FooterQuery, NavbarQuery, PageBySlugQuery, PageSectionsBySystemIdQuery } from 'types/generated';
import { chunk } from '@/utils/arrays';
import { getOr } from '@/utils/options';
import config from '@/config/config';
import PageContainer from '@/components/PageContainer';
import FrelsiFyllaPage from '@/contentful/FrelsiFyllaPage';
import { getClient } from '@/utils/contentful';
import { getRadioSchedules } from '@/clients/RadioUtils';
import { RadioSchedule } from '@/models/RadioTypes';
import { Log } from '@/logging/datadog';

export const slug = 'en/topup';

type Props = {
  page: PageBySlugQuery;
  navbar: NavbarQuery;
  footer: FooterQuery;
  sections: Array<PageSectionsBySystemIdQuery>;
  radioSchedules: Record<string, RadioSchedule[]>;
  orderedIds: Array<string>;
};

const RouteComponent = ({ navbar, footer, page, sections, radioSchedules, orderedIds }: Props) => {
  const pageCollection = page.pageCollection?.items[0];

  if (!pageCollection) {
    Log.error('no page collection found for /topup');
    return {
      notFound: true,
    };
  }

  return (
    <PageContainer
      title={getOr(pageCollection.title, 'Sýn')}
      metaTags={pageCollection.metaTags}
      contactFormSetting={pageCollection.contactFormSetting}
      navbar={navbar}
      footer={footer}
      radioSchedules={radioSchedules}
    >
      <FrelsiFyllaPage sections={sections} orderedIds={orderedIds} />
    </PageContainer>
  );
};

export const getStaticProps: GetStaticProps = async (context) => {
  const inPreview = getOr(context.draftMode, false);
  try {
    const client = getClient(inPreview);

    const contentfulPage = (await queryPageBySlug(client, slug, inPreview)).data;

    if (!contentfulPage.pageCollection) {
      Log.error(`no page collection found in graphql response when fetching page details for ${slug}`);
      return {
        notFound: true,
      };
    }

    if (!contentfulPage.pageCollection.items) {
      Log.error(`no items array set in page details for ${slug}`);
      return {
        notFound: true,
      };
    }

    const page = contentfulPage.pageCollection.items[0] || undefined;

    if (!page) {
      Log.error(`no page component found in graphql response for slug ${slug}`);
      return {
        notFound: true,
      };
    }

    const sectionIds = (page.sectionsCollection?.items.map((x) => x?.sys.id) || []) as string[];

    const chunkedSections = chunk(sectionIds, config.SECTION_CHUNK_SIZE).map((c) => {
      return querySectionsBySystemIds(client, c, inPreview).then((x) => x.data);
    });

    return {
      props: {
        navbar: (await queryNavbar(client)).data,
        footer: (await queryFooter(client)).data,
        page: contentfulPage,
        sections: await Promise.all(chunkedSections),
        radioSchedules: await getRadioSchedules(),
        orderedIds: sectionIds,
      },
    };
  } catch (err) {
    Log.error((err as Error).message || '', err);
    throw err;
  }
};

export default RouteComponent;
